import ReactPlayer from 'react-player/lazy';
import { MouseEvent, ReactElement, useEffect, useState } from 'react';
import styles from './Player.module.scss';
import classNames from 'classnames';

export interface UI_PlayerProps {
	url: string;
	isLight?: boolean;
	poster?: string;
	playing?: boolean;
	loop?: boolean;
	controls?: boolean;
	cover?: boolean;
	muted?: boolean;
	playIcon?: ReactElement;
	preload?: 'none' | 'metadata' | 'auto';
	showPauseButton?: boolean;
	pauseLabel?: string;
	playLabel?: string;
}

// The translated labels for the pause button, read from the dictionary by the Website features and passed down
export type UI_PlayerLabels = Pick<UI_PlayerProps, 'pauseLabel' | 'playLabel'>;

export const UI_Player = ({
	preload,
	url,
	muted = false,
	isLight = true,
	poster,
	playing = false,
	loop = false,
	controls = true,
	cover,
	playIcon,
	showPauseButton = true,
	pauseLabel = 'Pause video',
	playLabel = 'Play video',
}: UI_PlayerProps) => {
	const light = !isLight ? false : poster ? poster : true;
	const className = classNames(styles.Player, { [styles.Player___cover]: cover });

	/**
	 * react-player swaps its Suspense boundary for `() => null` when there is no window
	 * (UniversalSuspense in react-player/lib/ReactPlayer.js), so the server emits an empty
	 * wrapper while the client fills it with the preview or the player. Holding back the
	 * player until after mount keeps the first client render identical to the server output,
	 * which is what hydration compares. The placeholder keeps the aspect ratio box reserved
	 * so nothing shifts when the player takes over.
	 */
	const [isMounted, setIsMounted] = useState(false);

	/**
	 * WCAG 2.2.2 (Pause, Stop, Hide): moving content that starts by itself and runs for more
	 * than five seconds must be pausable. Without native controls the user has no way to stop
	 * the video, so we render our own toggle. The pause is kept even if the parent toggles
	 * `playing` (e.g. scrolling out of view and back), so the user's choice is respected.
	 */
	const [isPausedByUser, setIsPausedByUser] = useState(false);

	/**
	 * Users who have turned on "reduce motion" in their OS should not get video that moves by itself,
	 * so autoplaying videos start paused and the toggle offers "play" instead. It is read once on mount,
	 * so it never overrides a later choice the user makes with the toggle or the native controls.
	 */
	const [prefersReducedMotion, setPrefersReducedMotion] = useState(false);

	// Mirrors react-player's own `showPreview` state, so the toggle stays hidden while only the poster is shown.
	const [hasClickedPreview, setHasClickedPreview] = useState(false);
	const [prevIsLight, setPrevIsLight] = useState(isLight);
	if (isLight !== prevIsLight) {
		setPrevIsLight(isLight);
		if (isLight) setHasClickedPreview(false);
	}
	const isShowingPreview = isLight && !hasClickedPreview;
	const hasPauseButton = showPauseButton && !controls && !isShowingPreview && (playing || isPausedByUser);

	useEffect(() => {
		setIsMounted(true);
		// Set in the same effect as isMounted, so the player's first render is already paused and never starts playing.
		if (window.matchMedia?.('(prefers-reduced-motion: reduce)').matches) {
			setPrefersReducedMotion(true);
			setIsPausedByUser(true);
		}
	}, []);

	if (!isMounted) {
		return (
			<div className={styles.Player_wrapper}>
				<div className={className} style={{ width: '100%', height: '100%' }} aria-hidden="true" />
			</div>
		);
	}

	const onTogglePause = (event: MouseEvent<HTMLButtonElement>) => {
		// The player is often rendered inside a card link, so the click must not navigate.
		event.preventDefault();
		event.stopPropagation();
		setIsPausedByUser((isPaused) => !isPaused);
	};

	return (
		<div className={styles.Player_wrapper}>
			<ReactPlayer
				url={url}
				className={className}
				light={light}
				muted={muted}
				playing={playing && !isPausedByUser}
				loop={loop}
				controls={controls}
				width="100%"
				height="100%"
				playsinline={true}
				playIcon={playIcon}
				onClickPreview={() => {
					// Clicking the preview is an explicit request to play, also for users who prefer reduced motion.
					setHasClickedPreview(true);
					setIsPausedByUser(false);
				}}
				// Keeps the toggle in sync when the video is started from the native controls.
				onPlay={() => setIsPausedByUser(false)}
				config={{
					youtube: {
						playerVars: { modestbranding: 1 },
					},
					file: {
						attributes: {
							preload,
							// A video held back by reduced motion would otherwise show a blank or first frame (iOS shows nothing).
							poster: prefersReducedMotion ? poster : undefined,
						},
					},
				}}
			/>
			{hasPauseButton && (
				<button
					type="button"
					className={styles.Player_pauseButton}
					// Safari (and Firefox on macOS) skip <button> when tabbing unless "Full Keyboard Access" is on;
					// an explicit tabindex keeps the toggle in the tab order for every keyboard user.
					tabIndex={0}
					onClick={onTogglePause}
					aria-label={isPausedByUser ? playLabel : pauseLabel}
				>
					<span className={classNames(styles.Player_pauseIcon, { [styles.Player_pauseIcon___play]: isPausedByUser })} aria-hidden="true" />
				</button>
			)}
		</div>
	);
};
